import React, { useRef, useState, lazy, Suspense, forwardRef } from 'react'
import { ListItem, MenuOverlay, Button, Separator, useOverlay, themes, Theme, IconButton } from '@fs/zion-ui'
import { EscHandlerProvider } from '@fs/zion-ui/dist/es/use-esc-handler/EscHandlerProvider'
import { useTranslation } from 'react-i18next'
import { Menu as MenuIcon, PlaceWorld } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useUser } from '@fs/zion-user'
import { trackLink } from '@fs/zion-analytics'
import { i18n } from '@fs/zion-locale'
import { H5 } from '../type/type'
import RainbowDivider from '../RainbowDivider'
import useIsMobile from '../../hooks/useIsMobile'
import RootsTechOverlay from '../RootsTechOverlay'
import { useChatDispatch, useChatState } from '../../state/ChatState'
import findLang from '../../global/find-lang'
import { useClearAllUnreadMessagesFromCache } from '../../hooks/useUnreadMessageCache'
import { loginUserRTCCampaignID } from '../../global/campainIds'
import useShowSignUpDialog from '../../hooks/useShowSignUpDialog'

const LangPicker = lazy(() => import('@fs/zion-lang-picker'))

const buttonOverrideCss = css`
  & button,
  & a {
    color: white !important;
    display: flex;
    flex-direction: row-reverse;
    transform: unset !important;
  }
  & button,
  & a {
    transform: translateX(-12px);
    text-transform: initial !important;
    letter-spacing: 0.035em !important;
  }
  & div[aria-hidden] {
    margin-right: 0px !important;
  }
  &:not([is-mobile]) {
    & button,
    & a {
      padding-right: 8px;
      padding-left: 12px !important;
    }
    & svg {
      margin-left: 8px;
    }
  }
`

const menuHeaderCss = css`
  min-width: 280px;
  padding: 16px 28px;

  &[is-mobile] {
    padding: 0;
  }
`

const menuOverridesCss = css`
  /* Override the padding in the list items to match designs */
  /* This is fragile. If they change the menu overlay at all, this may break. */
  padding: 4px 0;
  &[is-mobile] {
    padding: 0;
    --background-primary: #2e2e2e !important;
  }
  & li div[interactable] {
    padding: 8px 28px;
  }
  & ul {
    padding: 8px 0;
  }
  & *[role='menu'],
  & *[data-testid='popper'] {
    z-index: 11 !important;
  }
`
const mobileListCss = css`
  & > div {
    --background-primary: #2e2e2e !important;
  }
  & li div[interactable] {
    background-color: inherit;
    padding: 0 !important;
  }
`

function MenuButtonBase(props, ref) {
  const [t] = useTranslation()
  const isMobile = useIsMobile()
  return (
    <div css={buttonOverrideCss} is-mobile={boolAttr(isMobile)}>
      {isMobile ? (
        <IconButton Icon={MenuIcon} {...props} ref={ref} />
      ) : (
        <Button Icon={MenuIcon} {...props} ref={ref}>
          {t('rootstech-connect.menu', 'Menu')}
        </Button>
      )}
    </div>
  )
}
export const MenuButton = forwardRef(MenuButtonBase)

// onClick is used by MobileMenu to make sure the menu is closed when an item is selected
function MenuContent({ isMobile, showNavItems, onClick, openLangPicker }) {
  const [t] = useTranslation()
  const user = useUser()
  const { connection } = useChatState()
  const dispatch = useChatDispatch()
  const [clearUnreadMessagesCache] = useClearAllUnreadMessagesFromCache()
  const openSignUpDialog = useShowSignUpDialog()

  const langText = findLang(i18n.language, t)

  const handleSignOut = () => {
    connection?.disconnect()
    clearUnreadMessagesCache()
    dispatch({ type: 'LOGOUT' })
  }

  return (
    <Theme theme={themes.nightfall}>
      <div css={menuOverridesCss} is-mobile={boolAttr(isMobile)}>
        {isMobile && (
          <>
            <div css={menuHeaderCss} is-mobile={boolAttr(isMobile)}>
              <H5 as="h1">{t('rootstech-connect.menu', 'Menu')}</H5>
            </div>
            <Separator />
            <RainbowDivider full={isMobile} flavor="blueberry" />
            <Separator size="xxs" />
          </>
        )}

        {showNavItems && (
          <>
            <ListItem
              data-testid="menu-home"
              onClick={onClick}
              primaryText={t('navigation.home.label', 'Home')}
              to="/home"
            />
            <ListItem
              data-testid="menu-guide-me"
              onClick={onClick}
              primaryText={t('navigation.series.label', 'Guide Me')}
              to="/series"
            />
            <ListItem
              data-testid="menu-search"
              onClick={onClick}
              primaryText={t('navigation.sessions.label', 'Sessions')}
              to="/search"
            />
            <ListItem
              data-testid="menu-my-playlist"
              onClick={(e) => {
                openSignUpDialog(e)
                onClick(e)
              }}
              primaryText={t('navigation.my-playlist.label', 'My Playlist')}
              to="/playlist"
            />
            <ListItem
              data-testid="menu-expo-hall"
              onClick={onClick}
              primaryText={t('navigation.expo-hall.label', 'Expo Hall')}
              to="/expohall"
            />
          </>
        )}

        <ListItem
          data-testid="menu-speakers"
          onClick={onClick}
          primaryText={t('navigation.speakers.label', 'Speakers')}
          to="/speakers"
        />
        <ListItem
          data-testid="innovators-portal"
          onClick={onClick}
          primaryText={t('navigation.innovators-portal', 'Innovators Portal')}
          to="/series/innovators"
        />
        <ListItem data-testid="faq" onClick={onClick} primaryText={t('navigation.faq.label', 'FAQ')} to="/faq" />
        <ListItem
          data-testid="menu-lang-picker"
          onClick={openLangPicker}
          leftElement={<ListItem.Icon Icon={PlaceWorld} />}
          primaryText={t('navigation.language.label', 'Language - {lang}', { lang: langText })}
        />
        {user.signedIn && (
          <ListItem
            data-testid="menu-sign-out"
            onClick={handleSignOut}
            primaryText={t('navigation.sign-out', 'Sign Out')}
            to="/auth/logout?returnUrl=/rootstech/rtc2021"
            external
          />
        )}
        {!user.signedIn && (
          <ListItem
            data-testid="menu-sign-in"
            primaryText={t('common-ui:sign-in.action', 'Sign In')}
            to={`/auth/familysearch/login?returnUrl=${encodeURIComponent(
              window.location.pathname
            )}?cid=${loginUserRTCCampaignID}`}
            external
            onClick={() => {
              trackLink(null, '/auth/familysearch/login', 'click_action', 'lo_hdr19_sign_in')
              onClick()
            }}
          />
        )}
      </div>
    </Theme>
  )
}

function MobileMenu({ showNavItems, onLangClick }) {
  const [isOpen, setIsOpen] = useState(false)
  const menuButtonRef = useRef()
  return (
    <>
      <MenuButton data-testid="mobile-menu-button" ref={menuButtonRef} onClick={() => setIsOpen(true)} />
      <RootsTechOverlay isOpen={isOpen} setIsOpen={setIsOpen} focusBackRef={menuButtonRef}>
        <ul css={mobileListCss} aria-label="Submenu">
          <MenuContent
            isMobile
            showNavItems={showNavItems}
            onClick={() => setIsOpen(false)}
            openLangPicker={() => {
              setIsOpen(false)
              onLangClick()
            }}
          />
        </ul>
        {/* This separator adds enough space at the bottom to prevent the "Sign In" button being hidden by the bottom bar on safari in iOS. */}
        <Separator size="lg" />
      </RootsTechOverlay>
    </>
  )
}

export default function Menu({ showNavItems }) {
  const isMobile = useIsMobile()
  const [renderLangPicker, setRenderLangPicker] = useState(false)
  const langOverlay = useOverlay()
  const menuOverlay = useOverlay()

  const onLangClick = (event) => {
    setRenderLangPicker(true)
    langOverlay.handleClick(event)
  }

  return isMobile ? (
    <EscHandlerProvider>
      <MobileMenu showNavItems={showNavItems} onLangClick={onLangClick} />
      <Suspense fallback={<span />}>{renderLangPicker && <LangPicker overlay={langOverlay} />}</Suspense>
    </EscHandlerProvider>
  ) : (
    <>
      <MenuButton data-testid="menu-button" onClick={menuOverlay.handleClick} />
      <MenuOverlay inline={false} {...menuOverlay} aria-label="Submenu">
        <MenuContent showNavItems={showNavItems} openLangPicker={onLangClick} onClick={menuOverlay.handleClick} />
      </MenuOverlay>

      <Suspense fallback={<span />}>{renderLangPicker && <LangPicker overlay={langOverlay} />}</Suspense>
    </>
  )
}
